import { useMemo, useState } from "react";
import './DocumentCenter.css'; // Importiere die CSS-Datei für die Stile
import { toast } from "react-toastify";

// Typdefinitionen
export type DocumentCategory =
    | "vertraege"
    | "medizinisch"
    | "versicherungen"
    | "eigene";

export type DocumentStatus = "vorlage" | "hochgeladen" | "geteilt" | "processing_upload" | "processing_ocr" | "review_required" | "accepted" | "failed" | "erstellt";

export type ProcessingStep = "upload" | "ocr" | "classification" | "field_detection" | "validation";

export type ExtractedField = {
    id: string;
    label: string;
    value: string;
    confidence: number; // 0-100
    sourcePage?: number;
    sourceArea?: string;
    isEditable?: boolean;
    type?: "text" | "select" | "date";
    options?: string[];
};

export type ExtractedData = {
    documentType?: string;
    fields: ExtractedField[];
    deviations?: string[]; // e.g. "abweichend zum Profil: Kasse war 'TK'"
};

export type DocumentPayload = {
    id: string;
    title: string;
    description: string;
    category: DocumentCategory;
    fileUrl: string;
    fileSize: string;
    lastUpdated: string;
    status: DocumentStatus;
    uploadedBy?: string;
    sharedWith?: string[];
    processingProgress?: number; // 0-100
    currentProcessingStep?: ProcessingStep;
    extractedData?: ExtractedData;
};

export type CareService = {
    id: string;
    name: string;
    email: string;
    phone?: string;
};

export type ShareRecipient = {
    id: string;
    name: string;
    email: string;
    phone?: string;
    type: "pflegedienst" | "patient" | "angehöriger" | "versicherung";
    insuranceType?: "GKV" | "PKV" | "DRV";
};

export type TemplateField = {
    id: string;
    label: string;
    type: "text" | "textarea" | "select" | "date" | "checkbox";
    required: boolean;
    options?: string[];
    defaultValue?: string;
    placeholder?: string;
};

export type TemplateFormData = {
    [key: string]: string | boolean | string[];
};

export type TaskAssignment = {
    id: string;
    title: string;
    description: string;
    assignedTo: "klient" | "angehöriger" | "mitarbeiter";
    dueDate?: string;
    priority: "low" | "medium" | "high";
    completed: boolean;
};

export type PatientData = {
    firstName: string;
    lastName: string;
    versicherungsnummer?: string;
    krankenkasse?: string;
    clientAvailability: string;
    bedarfsort: string;
    addresse: string;
    phoneNumber: string;
    requestRegion: string;
    requestZipCode: string;
    productCriteriaList: Array<{
        criteriaName: string;
        value: string;
    }>;
};

// ---- Statische Defaults und Dummy Daten ----
const DEFAULT_CARE_SERVICES: CareService[] = [
    { id: "pflegedienst_a", name: "Pflegedienst Sonnenschein", email: "info@sonnenschein.de", phone: "030-12345678" },
    { id: "pflegedienst_b", name: "Ambulant Pflegedienst", email: "kontakt@ambulant.de", phone: "040-98765432" },
    { id: "pflegedienst_c", name: "Pflegehilfe 24/7", email: "service@pflegehilfe247.de", phone: "069-11223344" },
    { id: "pflegedienst_d", name: "Mobile Pflege GmbH", email: "hallo@mobile-pflege.de", phone: "089-55667788" },
];

const SHARE_RECIPIENTS: ShareRecipient[] = [

    // Patient
    { id: "patient", name: "Max Mustermann (Patient)", email: "max.mustermann@email.de", phone: "017621231523", type: "patient" },

    // Angehörige
    { id: "angehöriger_1", name: "Erika Mustermann (Ehefrau)", email: "erika.mustermann@email.de", phone: "0123456790", type: "angehöriger" },
    { id: "angehöriger_2", name: "Hans Mustermann (Sohn)", email: "hans.mustermann@email.de", phone: "0123456791", type: "angehöriger" },

    // Versicherungen
    { id: "gkv_aok", name: "AOK - Die Gesundheitskasse", email: "service@aok.de", phone: "0800-265080", type: "versicherung", insuranceType: "GKV" },
    { id: "pkv_allianz", name: "Allianz Care", email: "care@allianz.de", phone: "0800-1122334", type: "versicherung", insuranceType: "PKV" },
    { id: "drv_bund", name: "Deutsche Rentenversicherung Bund", email: "info@drv-bund.de", phone: "0800-10004810", type: "versicherung", insuranceType: "DRV" },


    // Pflegedienste
    { id: "pflegedienst_a", name: "Pflegedienst Sonnenschein", email: "info@sonnenschein.de", phone: "030-12345678", type: "pflegedienst" },
    { id: "pflegedienst_b", name: "Ambulant Pflegedienst", email: "kontakt@ambulant.de", phone: "040-98765432", type: "pflegedienst" },
    { id: "pflegedienst_c", name: "Pflegehilfe 24/7", email: "service@pflegehilfe247.de", phone: "069-11223344", type: "pflegedienst" },
    { id: "pflegedienst_d", name: "Mobile Pflege GmbH", email: "hallo@mobile-pflege.de", phone: "089-55667788", type: "pflegedienst" },

];

const DOCUMENT_TEMPLATES: DocumentPayload[] = [
    {
        id: "vertrag_pflege",
        title: "Pflegevertrag Häusliche Pflege",
        description: "Pflegevertrag für häusliche Pflegeleistungen",
        category: "vertraege",
        fileUrl: "./pflege_pflegevertrag_hausliche_pflege.pdf",
        fileSize: "342 KB",
        lastUpdated: "2024-01-20",
        status: "vorlage"
    },
    {
        id: "vertrag_dienstleistung",
        title: "Dienstleistungsvertrag",
        description: "Vertrag über spezifische Pflegeleistungen",
        category: "vertraege",
        fileUrl: "/dummy-docs/dienstleistungsvertrag.pdf",
        fileSize: "189 KB",
        lastUpdated: "2024-01-12",
        status: "vorlage"
    },
    {
        id: "kostenvoranschlag",
        title: "Kostenvoranschlag Vorlage",
        description: "Vorlage für Kostenvoranschläge",
        category: "vertraege",
        fileUrl: "/dummy-docs/kostenvoranschlag.pdf",
        fileSize: "156 KB",
        lastUpdated: "2024-01-10",
        status: "vorlage"
    },
    {
        id: "medikationsplan",
        title: "Medikationsplan Vorlage",
        description: "Standardisierter Medikationsplan",
        category: "medizinisch",
        fileUrl: "/dummy-docs/medikationsplan.pdf",
        fileSize: "180 KB",
        lastUpdated: "2024-01-14",
        status: "vorlage"
    },
    {
        id: "pflegeplan",
        title: "Pflegeplan Muster",
        description: "Umfassender Pflegeplan für Patienten",
        category: "medizinisch",
        fileUrl: "/dummy-docs/pflegeplan.pdf",
        fileSize: "298 KB",
        lastUpdated: "2024-01-13",
        status: "vorlage"
    },
    {
        id: "arztbericht",
        title: "Arztbericht Vorlage",
        description: "Standardformular für Arztberichte",
        category: "medizinisch",
        fileUrl: "/dummy-docs/arztbericht.pdf",
        fileSize: "167 KB",
        lastUpdated: "2024-01-11",
        status: "vorlage"
    },
    {
        id: "einwilligung",
        title: "Einwilligungserklärung",
        description: "Patienteneinwilligung für Behandlungen",
        category: "medizinisch",
        fileUrl: "/dummy-docs/einwilligung.pdf",
        fileSize: "134 KB",
        lastUpdated: "2024-01-16",
        status: "vorlage"
    },
    {
        id: "pflegegrad_antrag",
        title: "Pflegegrad Antrag",
        description: "Antrag auf Einstufung des Pflegegrades",
        category: "versicherungen",
        fileUrl: "/dummy-docs/pflegegrad_antrag.pdf",
        fileSize: "276 KB",
        lastUpdated: "2024-01-09",
        status: "vorlage"
    },
    {
        id: "kostenerstattung",
        title: "Kostenerstattungsantrag",
        description: "Antrag auf Kostenerstattung von Leistungen",
        category: "versicherungen",
        fileUrl: "/dummy-docs/kostenerstattung.pdf",
        fileSize: "203 KB",
        lastUpdated: "2024-01-08",
        status: "vorlage"
    },
    {
        id: "leistungsnachweis",
        title: "Leistungsnachweis",
        description: "Nachweis über erbrachte Pflegeleistungen",
        category: "versicherungen",
        fileUrl: "/dummy-docs/leistungsnachweis.pdf",
        fileSize: "189 KB",
        lastUpdated: "2024-01-07",
        status: "vorlage"
    }
];

// Vorlagen-Konfiguration mit Formularfeldern
const TEMPLATE_CONFIGS: Record<string, TemplateField[]> = {
    vertrag_pflege: [
        { id: "klient_name", label: "Klientenname", type: "text", required: true, placeholder: "Vor- und Nachname" },
        { id: "klient_adresse", label: "Adresse", type: "textarea", required: true, placeholder: "Straße, PLZ, Ort" },
        { id: "klient_telefon", label: "Telefonnummer", type: "text", required: true, placeholder: "Telefonnummer" },
        { id: "pflegegrad", label: "Pflegegrad", type: "select", required: true, options: ["1", "2", "3", "4", "5"] },
        { id: "krankenkasse", label: "Krankenkasse", type: "text", required: true, placeholder: "Name der Krankenkasse" },
        { id: "versicherungsnummer", label: "Versicherungsnummer", type: "text", required: false, placeholder: "Versicherungsnummer" },
        { id: "beginn_datum", label: "Beginn der Pflege", type: "date", required: true },
        { id: "leistungen", label: "Art der Leistungen", type: "select", required: true, options: ["Häusliche Pflege", "Grundpflege", "Behandlungspflege", "Betreuung"] },
        { id: "besondere_vereinbarungen", label: "Besondere Vereinbarungen", type: "textarea", required: false, placeholder: "Zusätzliche Vereinbarungen..." },
    ],
    medikationsplan: [
        { id: "klient_name", label: "Klientenname", type: "text", required: true, placeholder: "Vor- und Nachname" },
        { id: "geburtsdatum", label: "Geburtsdatum", type: "date", required: true },
        { id: "hausarzt", label: "Hausarzt", type: "text", required: true, placeholder: "Name des Hausarztes" },
        { id: "diagnosen", label: "Hauptdiagnosen", type: "textarea", required: true, placeholder: "Medizinische Diagnosen..." },
        { id: "allergien", label: "Allergien/Unverträglichkeiten", type: "textarea", required: false, placeholder: "Bekannte Allergien..." },
        { id: "medikamente", label: "Aktuelle Medikamente", type: "textarea", required: true, placeholder: "Medikament, Dosierung, Einnahmezeit..." },
        { id: "besondere_hinweise", label: "Besondere Hinweise", type: "textarea", required: false, placeholder: "Besondere Hinweise zur Medikation..." }
    ],
    einwilligung: [
        { id: "klient_name", label: "Klientenname", type: "text", required: true, placeholder: "Vor- und Nachname" },
        { id: "geburtsdatum", label: "Geburtsdatum", type: "date", required: true },
        { id: "behandlung_art", label: "Art der Behandlung", type: "select", required: true, options: ["Pflege", "Therapie", "Untersuchung", "Operation"] },
        { id: "aufklaerung_datum", label: "Datum der Aufklärung", type: "date", required: true },
        { id: "aufklaerung_durch", label: "Aufklärung durch", type: "text", required: true, placeholder: "Name des aufklärenden Personals" },
        { id: "einwilligungstext", label: "Einwilligungstext", type: "textarea", required: true, placeholder: "Text der Einwilligungserklärung..." },
        { id: "besondere_wuensche", label: "Besondere Wünsche", type: "textarea", required: false, placeholder: "Besondere Wünsche des Patienten..." }
    ]
};

// Patientendaten für Vorausfüllung
const PATIENT_DATA: PatientData = {
    firstName: "Max",
    lastName: "Mustermann",
    clientAvailability: "Mo-Fr 9-17 Uhr",
    versicherungsnummer: "A123456789",
    krankenkasse: "AOK",
    bedarfsort: "Mainz, 55116",
    addresse: "Musterstraße 1, 55116 Mainz",
    phoneNumber: "017621231523",
    requestRegion: "Mainz",
    requestZipCode: "55116",
    productCriteriaList: [
        { criteriaName: "Pflegegrad/-stufe", value: "3" },
        { criteriaName: "Aufgaben", value: "Grundpflege + Behandlungspflege" },
        { criteriaName: "Betreuungsbedarf – Wöchentlicher Umfang", value: "Täglich" },
        { criteriaName: "Zeitpunkt – Bedarf", value: "Schnellstmöglich" }
    ]
};

// 4 Hauptkategorien (Kacheln)
const CATEGORY_OPTIONS: { value: DocumentCategory; label: string; emoji: string; count: number }[] = [
    { value: "vertraege", label: "Verträge & Formulare", emoji: "📄", count: 4 },
    { value: "medizinisch", label: "Medizinische Dokumente", emoji: "🏥", count: 4 },
    { value: "versicherungen", label: "Versicherungen & Leistungen", emoji: "🛡️", count: 3 },
    { value: "eigene", label: "Eigene Dokumente", emoji: "📁", count: 0 },
];

// ---- UI Atoms ----
function DocumentTile({
    title,
    subtitle,
    active,
    onClick,
    emoji,
    disabled,
    count,
}: {
    title: string;
    subtitle?: string;
    emoji?: string;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    count?: number;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="doc-tile"
            aria-pressed={!!active}
        >
            <div className="doc-tile-content">
                {emoji && <span className="doc-tile-emoji">{emoji}</span>}
                <div className="doc-tile-text">
                    <span className="doc-tile-title">{title}</span>
                    {subtitle && <span className="doc-tile-subtitle">{subtitle}</span>}
                    {count !== undefined && (
                        <span className="doc-tile-count">{count} Dokumente</span>
                    )}
                </div>
            </div>
        </button>
    );
}

function DocumentItem({
    document,
    onView,
    onDownload,
    onShare,
    onDelete,
    onFillTemplate,
    onReviewResult,
    onAcceptResult,
    onRetryProcessing,
}: {
    document: DocumentPayload;
    onView: () => void;
    onDownload: () => void;
    onShare: () => void;
    onDelete: () => void;
    onFillTemplate?: () => void;
    onReviewResult?: () => void;
    onAcceptResult?: () => void;
    onRetryProcessing?: () => void;
}) {
    const getStatusBadge = (status: DocumentStatus) => {
        switch (status) {
            case "vorlage": return <span className="status-badge template">Vorlage</span>;
            case "hochgeladen": return <span className="status-badge uploaded">Hochgeladen</span>;
            case "geteilt": return <span className="status-badge shared">Geteilt</span>;
            case "processing_upload": return <span className="status-badge processing-upload">🟦 Wird hochgeladen...</span>;
            case "processing_ocr": return <span className="status-badge processing-ocr">🟡 Wird verarbeitet</span>;
            case "review_required": return <span className="status-badge review-required">🟠 Prüfung erforderlich</span>;
            case "accepted": return <span className="status-badge accepted">🟩 Übernommen</span>;
            case "failed": return <span className="status-badge failed">🔺 Fehlgeschlagen</span>;
            case "erstellt": return <span className="status-badge created">Erstellt</span>;
            default: return null;
        }
    };

    const getProcessingSubBadges = (step?: ProcessingStep) => {
        if (!step) return null;

        const steps: ProcessingStep[] = ["upload", "ocr", "classification", "field_detection", "validation"];
        const currentIndex = steps.indexOf(step);

        return (
            <div className="processing-sub-badges">
                {steps.map((s, index) => (
                    <span
                        key={s}
                        className={`sub-badge ${index <= currentIndex ? 'active' : ''}`}
                    >
                        {s === "upload" && "Upload"}
                        {s === "ocr" && "OCR"}
                        {s === "classification" && "Klassifizierung"}
                        {s === "field_detection" && "Felderkennung"}
                        {s === "validation" && "Validierung"}
                    </span>
                ))}
            </div>
        );
    };

    const getProgressPercentage = (progress?: number) => {
        if (typeof progress === 'number') {
            return `${Math.round(progress)}%`;
        }
        return null;
    };

    const getExtractedDataSummary = (data?: ExtractedData) => {
        if (!data || !data.fields) return null;

        const summaries = data.fields
            .filter(field => field.confidence >= 90) // Nur hochvertrauensliche Felder anzeigen
            .map(field => `${field.label}: ${field.value}`)
            .slice(0, 3); // Maximal 3 Felder anzeigen

        return summaries.length > 0 ? summaries.join(", ") : null;
    };

    const isProcessing = document.status === "processing_upload" || document.status === "processing_ocr";
    const canReview = document.status === "review_required";
    const canAccept = document.status === "accepted";
    const hasFailed = document.status === "failed";

    return (
        <div className={`document-item ${isProcessing ? 'processing' : ''}`}>
            <div className="document-item-header">
                <div className="document-item-info">
                    <h4 className="document-item-title">{document.title}</h4>
                    <p className="document-item-description">{document.description}</p>
                    <div className="document-item-meta">
                        <span className="file-size">{document.fileSize}</span>
                        <span className="last-updated">Aktualisiert: {document.lastUpdated}</span>
                        {getStatusBadge(document.status)}
                        {isProcessing && document.currentProcessingStep && getProcessingSubBadges(document.currentProcessingStep)}
                        {isProcessing && document.processingProgress !== undefined && (
                            <span className="progress-percentage">{getProgressPercentage(document.processingProgress)}</span>
                        )}
                    </div>

                    {/* Fortschrittsbalken für die Verarbeitung */}
                    {isProcessing && document.processingProgress !== undefined && (
                        <div className="processing-progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${document.processingProgress}%` }}
                            ></div>
                        </div>
                    )}

                    {/* Zusammenfassung der extrahierten Daten für akzeptierte Dokumente */}
                    {canAccept && document.extractedData && (
                        <div className="extracted-data-summary">
                            {getExtractedDataSummary(document.extractedData)}
                        </div>
                    )}

                    {/* Kurztext für Review-Status */}
                    {canReview && document.extractedData && (
                        <div className="review-status-text">
                            {document.extractedData.fields.length} Felder erkannt. Bitte prüfen & bestätigen.
                        </div>
                    )}

                    {/* Kurztext für Fehler-Status */}
                    {hasFailed && (
                        <div className="failed-status-text">
                            Dokument unlesbar/unklar.
                        </div>
                    )}
                </div>
            </div>

            <div className="document-item-actions">
                <button className="btn-icon" onClick={onView} title="Anzeigen">
                    👁️
                </button>
                <button className="btn-icon" onClick={onDownload} title="Herunterladen">
                    ⬇️
                </button>
                <button className="btn-icon" onClick={onShare} title="Teilen">
                    📤
                </button>

                {/* Vorlage ausfüllen */}
                {document.status === "vorlage" && onFillTemplate && (
                    <button className="btn-icon template" onClick={onFillTemplate} title="Ausfüllen">
                        ✏️
                    </button>
                )}

                {/* Löschen (nur für hochgeladene Dokumente ohne Verarbeitung) */}
                {document.status === "hochgeladen" && (
                    <button className="btn-icon delete" onClick={onDelete} title="Löschen">
                        🗑️
                    </button>
                )}

                {/* Ergebnis ansehen (für Review und Accepted) */}
                {(canReview || canAccept) && onReviewResult && (
                    <button
                        className={`btn-icon ${canReview ? 'review' : 'view'}`}
                        onClick={onReviewResult}
                        title={canReview ? "Ergebnis ansehen" : "Details anzeigen"}
                    >
                        📊
                    </button>
                )}

                {/* Übernehmen (nur für Review Required) */}
                {canReview && onAcceptResult && (
                    <button className="btn-icon accept" onClick={onAcceptResult} title="Übernehmen">
                        ✓
                    </button>
                )}

                {/* Erneut versuchen (für Failed) */}
                {hasFailed && onRetryProcessing && (
                    <button className="btn-icon retry" onClick={onRetryProcessing} title="Erneut versuchen">
                        🔄
                    </button>
                )}

                {/* Löschen (für Processing, Failed, etc.) */}
                {(isProcessing || hasFailed || canReview || canAccept) && (
                    <button className="btn-icon delete" onClick={onDelete} title="Löschen">
                        🗑️
                    </button>
                )}
            </div>
        </div>
    );
}

function StickyActionBar({
    title,
    onBack,
    onAction,
    actionLabel,
    canAction,
}: {
    title: string;
    onBack?: () => void;
    onAction?: () => void;
    actionLabel?: string;
    canAction?: boolean;
}) {
    return (
        <div
            role="region"
            aria-label="Aktionen"
            className="sticky-action-bar"
        >
            <div className="action-bar-content">
                <div className="action-bar-title">{title}</div>
                <div className="action-bar-buttons">
                    {onBack && (
                        <button type="button" className="btn btn-info btn-medium-small" onClick={onBack}>
                            Zurück
                        </button>
                    )}
                    {onAction && actionLabel && (
                        <button
                            type="button"
                            className="btn btn-primary btn-medium-small"
                            onClick={onAction}
                            disabled={!canAction}
                        >
                            {actionLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}




export {
    DEFAULT_CARE_SERVICES,
    SHARE_RECIPIENTS,
    DOCUMENT_TEMPLATES,
    TEMPLATE_CONFIGS,
    PATIENT_DATA,
    CATEGORY_OPTIONS,
    DocumentTile,
    DocumentItem,
    StickyActionBar

}
